import 'highlight.js/styles/github.css' // Add syntax highlighting CSS
import { ExternalLink } from 'lucide-react'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'

// Custom collapsible component with animation
const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <details
      className="my-4 overflow-hidden rounded-2xl border border-gray-200/50 bg-white shadow-sm"
      open={isOpen}
    >
      <summary
        className="flex cursor-pointer items-center gap-2 p-4 font-semibold text-gray-900 transition-colors hover:bg-gray-50"
        onClick={(e) => {
          e.preventDefault()
          setIsOpen(!isOpen)
        }}
      >
        <span className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>▶</span>
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </summary>
      {isOpen && (
        <div className="animate-in slide-in-from-top-2 border-t border-gray-200/50 p-4 duration-200">
          {children}
        </div>
      )}
    </details>
  )
}

interface EnhancedMarkdownProps {
  children: string
  className?: string
  style?: React.CSSProperties
  components?: Record<string, React.ComponentType<any>>
}

// Preprocess markdown to fix common table issues
function preprocessMarkdown(content: string): string {
  const lines = content.split('\n')
  const processed: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    // Check if this looks like a table header (with or without list indentation)
    if (trimmed.startsWith('|') && trimmed.endsWith('|') && i + 1 < lines.length) {
      const nextLine = lines[i + 1].trim()
      // Check if next line is a separator
      if (/^\|[\s\-:|]+\|$/.test(nextLine)) {
        // Add blank line before table if previous line was a list item
        if (i > 0) {
          const prevTrimmed = lines[i - 1].trim()
          if (/^\d+\./.test(prevTrimmed) || /^[-*+]/.test(prevTrimmed)) {
            processed.push('') // Add blank line to break out of list context
          }
        }

        // This is a table! Collect all rows
        const tableLines = []
        const headerCells = trimmed.split('|').filter((c) => c.trim() !== '')
        const expectedColumns = headerCells.length

        // Add header without indentation (break out of list)
        tableLines.push(trimmed)

        i++
        tableLines.push(lines[i].trim()) // Add separator

        // Collect data rows
        i++
        while (i < lines.length) {
          const rowLine = lines[i].trim()
          if (!rowLine.startsWith('|') || !rowLine.endsWith('|')) {
            break // End of table
          }

          const cells = rowLine.split('|').filter((c) => c.trim() !== '')
          // If row has correct column count, add it
          if (cells.length === expectedColumns) {
            tableLines.push(rowLine)
          } else if (cells.length < expectedColumns) {
            // Pad with empty cells
            const padded =
              '| ' +
              cells.map((c) => c.trim()).join(' | ') +
              ' |' +
              ' | '.repeat(expectedColumns - cells.length)
            tableLines.push(padded.trim())
          }

          i++
        }

        // Add the table with blank line after
        processed.push(...tableLines)
        processed.push('') // Blank line after table
        continue
      }
    }

    processed.push(line)
    i++
  }

  return processed.join('\n')
}

export function EnhancedMarkdown({
  children: content,
  className = '',
  style,
  components: customComponents = {},
}: EnhancedMarkdownProps) {
  // Preprocess content
  const processedContent = preprocessMarkdown(content)

  const defaultComponents = {
    // Enhanced details/summary with animation
    details: ({ children, ...props }: any) => {
      // Extract summary and content
      let summaryContent = 'Details'
      let bodyContent = children

      if (Array.isArray(children)) {
        const summaryChild = children.find(
          (child: any) => child?.type === 'summary' || child?.props?.node?.tagName === 'summary'
        )

        if (summaryChild) {
          summaryContent = summaryChild.props?.children || summaryChild.children || 'Details'
          bodyContent = children.filter((child: any) => child !== summaryChild)
        }
      }

      return (
        <CollapsibleSection
          title={typeof summaryContent === 'string' ? summaryContent : 'Details'}
          defaultOpen={props.open}
        >
          {bodyContent}
        </CollapsibleSection>
      )
    },

    // Skip summary since it's handled by details
    summary: () => null,

    // Enhanced code blocks with syntax highlighting
    code: ({ inline, className, children, ...props }: any) => {
      return inline ? (
        <code
          className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-800"
          {...props}
        >
          {children}
        </code>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },

    pre: ({ children, ...props }: any) => {
      const match = /language-(\w+)/.exec(children?.props?.className || '')
      const language = match ? match[1] : ''

      return (
        <div className="my-2 overflow-hidden rounded-2xl border border-gray-200/50 bg-gray-50">
          {language && (
            <div className="border-b border-gray-200/50 bg-gray-100 px-3 py-1.5 font-mono text-xs text-gray-600">
              {language}
            </div>
          )}
          <pre className="m-0 overflow-x-auto p-3" {...props}>
            {children}
          </pre>
        </div>
      )
    },

    // Styled headings
    h1: ({ children, ...props }: any) => (
      <h1
        className="mb-6 border-b border-gray-200 pb-2 text-3xl font-bold text-gray-900"
        {...props}
      >
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: any) => (
      <h2 className="mb-4 mt-8 text-2xl font-semibold text-gray-800" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3 className="mb-3 mt-6 text-xl font-semibold text-gray-800" {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }: any) => (
      <h4 className="mb-2 mt-4 text-lg font-semibold text-gray-800" {...props}>
        {children}
      </h4>
    ),

    // Styled paragraphs and text
    p: ({ children, ...props }: any) => (
      <p className="mb-4 leading-relaxed text-gray-700 last:mb-0" {...props}>
        {children}
      </p>
    ),

    // Enhanced lists
    ul: ({ children, ...props }: any) => (
      <ul className="mb-4 ml-6 list-disc space-y-1 text-gray-700 last:mb-0" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: any) => (
      <ol className="mb-4 ml-6 list-decimal space-y-1 text-gray-700 last:mb-0" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: any) => (
      <li className="text-gray-700" {...props}>
        {children}
      </li>
    ),

    // Enhanced blockquotes
    blockquote: ({ children, ...props }: any) => (
      <blockquote
        className="my-4 border-l-4 border-blue-500 bg-blue-50 py-2 pl-4 italic text-gray-700 last:mb-0"
        {...props}
      >
        {children}
      </blockquote>
    ),

    // Enhanced tables
    table: ({ children, ...props }: any) => (
      <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: any) => (
      <thead className="bg-gray-50" {...props}>
        {children}
      </thead>
    ),
    tbody: ({ children, ...props }: any) => (
      <tbody className="divide-y divide-gray-200 bg-white" {...props}>
        {children}
      </tbody>
    ),
    th: ({ children, ...props }: any) => (
      <th
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }: any) => (
      <td className="px-4 py-3 text-sm text-gray-700" {...props}>
        {children}
      </td>
    ),

    // Enhanced links
    a: ({ children, href, ...props }: any) => (
      <a
        href={href}
        className="inline-flex items-center gap-1 text-blue-600 underline hover:text-blue-800"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
        <ExternalLink className="inline h-3 w-3" />
      </a>
    ),

    // Horizontal rule
    hr: ({ ...props }: any) => <hr className="my-6 border-gray-300" {...props} />,

    // Strong and emphasis
    strong: ({ children, ...props }: any) => (
      <strong className="font-semibold text-gray-900" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }: any) => (
      <em className="italic text-gray-800" {...props}>
        {children}
      </em>
    ),
  }

  // Merge custom components with defaults
  const mergedComponents = { ...defaultComponents, ...customComponents }

  return (
    <div className={`prose prose-gray max-w-none ${className}`} style={style}>
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm, // GitHub Flavored Markdown (tables, strikethrough, task lists)
        ]}
        rehypePlugins={[
          rehypeHighlight, // Syntax highlighting for code blocks
        ]}
        components={mergedComponents}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

// Export alias for compatibility
export default EnhancedMarkdown
